import React, { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ShieldCheck, Trees, Trash2, Sparkles, ChevronRight, RotateCcw } from "lucide-react";

/**
 * Guided color-code wizard.
 * Presents 4 sequential questions leading to a proposed risk_level (red / yellow / green).
 * Users can restart or choose "Non sono sicuro" → yellow provvisorio.
 * Admins can still override in the report dashboard (not here).
 */
export default function ColorWizard({ value, onChange, initialAiRisk }) {
  const { t } = useI18n();
  const [step, setStep] = useState(0);
  const [choice, setChoice] = useState(value || initialAiRisk || null);
  const [reason, setReason] = useState(null); // stores the "why" chip

  const commit = (color, reasonKey) => {
    setChoice(color);
    setReason(reasonKey);
    onChange && onChange(color);
    setStep(99); // final
  };

  const restart = () => {
    setStep(0);
    setChoice(null);
    setReason(null);
    onChange && onChange(null);
  };

  if (step === 99 && choice) {
    return (
      <div className="rounded-2xl border-2 border-slate-200 bg-white p-4 space-y-3" data-testid="color-wizard-result">
        <div className="flex items-center gap-3">
          <span
            className="w-5 h-5 rounded-full shrink-0"
            style={{ background: choice === "red" ? "#EF4444" : choice === "yellow" ? "#EAB308" : "#10B981" }}
          />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-slate-900" data-testid="color-wizard-color">
              {choice === "red" ? t("cw_result_red") : choice === "yellow" ? t("cw_result_yellow") : t("cw_result_green")}
            </div>
            {reason && (
              <div className="text-xs text-slate-500 mt-0.5" data-testid="color-wizard-reason">{t(reason)}</div>
            )}
          </div>
          <button
            type="button"
            onClick={restart}
            data-testid="color-wizard-restart"
            className="text-xs font-semibold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          >
            <RotateCcw size={12} /> {t("cw_restart")}
          </button>
        </div>
        {choice === "red" && (
          <div className="text-xs bg-red-50 border border-red-200 rounded-lg p-2 text-red-800" data-testid="color-wizard-red-note">
            {t("cw_red_admin_note")}
          </div>
        )}
      </div>
    );
  }

  const Question = ({ testid, icon: Icon, tone, questionKey, exampleKey, onYes, onNo }) => (
    <div className={`rounded-2xl border-2 p-4 ${tone === "red" ? "border-red-200 bg-red-50/40" : tone === "yellow" ? "border-yellow-200 bg-yellow-50/40" : "border-slate-200 bg-white"}`} data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${tone === "red" ? "bg-red-500 text-white" : tone === "yellow" ? "bg-yellow-500 text-white" : "bg-slate-900 text-white"}`}>
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">{t("cw_step_label").replace("{n}", String(step + 1))}/4</div>
          <div className="text-sm font-semibold text-slate-900 leading-snug">{t(questionKey)}</div>
          <div className="mt-1.5 text-xs text-slate-600 leading-relaxed"><b>{t("cw_examples")}: </b>{t(exampleKey)}</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Button type="button" size="sm" data-testid={`${testid}-yes`} onClick={onYes} className={`rounded-full h-9 text-sm font-semibold ${tone === "red" ? "bg-red-600 hover:bg-red-700 text-white" : tone === "yellow" ? "bg-yellow-600 hover:bg-yellow-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}>
          {t("cw_yes")}
        </Button>
        <Button type="button" size="sm" variant="outline" data-testid={`${testid}-no`} onClick={onNo} className="rounded-full h-9 text-sm font-semibold hover:bg-slate-100">
          {t("cw_no")}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="color-wizard">
      <div className="text-xs text-slate-500" data-testid="color-wizard-intro">{t("cw_intro")}</div>

      {step === 0 && (
        <Question
          testid="cw-q1"
          icon={AlertTriangle}
          tone="red"
          questionKey="cw_q1"
          exampleKey="cw_q1_examples"
          onYes={() => commit("red", "cw_q1_reason")}
          onNo={() => setStep(1)}
        />
      )}
      {step === 1 && (
        <Question
          testid="cw-q2"
          icon={ShieldCheck}
          tone="red"
          questionKey="cw_q2"
          exampleKey="cw_q2_examples"
          onYes={() => commit("red", "cw_q2_reason")}
          onNo={() => setStep(2)}
        />
      )}
      {step === 2 && (
        <Question
          testid="cw-q3"
          icon={Trees}
          tone="yellow"
          questionKey="cw_q3"
          exampleKey="cw_q3_examples"
          onYes={() => commit("yellow", "cw_q3_reason")}
          onNo={() => setStep(3)}
        />
      )}
      {step === 3 && (
        <div className="rounded-2xl border-2 border-slate-200 bg-white p-4" data-testid="cw-q4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-emerald-600 text-white">
              <Trash2 size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">4/4</div>
              <div className="text-sm font-semibold text-slate-900 leading-snug">{t("cw_q4")}</div>
              <div className="mt-1.5 text-xs text-slate-600 leading-relaxed"><b>{t("cw_examples")}: </b>{t("cw_q4_examples")}</div>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button type="button" size="sm" data-testid="cw-q4-yes" onClick={() => commit("green", "cw_q4_reason")} className="rounded-full min-h-10 h-auto text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white py-1.5 whitespace-normal leading-tight">{t("cw_yes")}</Button>
            <Button type="button" size="sm" variant="outline" data-testid="cw-q4-no" onClick={() => commit("yellow", "cw_unsure_reason")} className="rounded-full min-h-10 h-auto text-xs font-semibold hover:bg-slate-100 py-1.5 whitespace-normal leading-tight">{t("cw_no")}</Button>
            <Button type="button" size="sm" variant="outline" data-testid="cw-q4-unsure" onClick={() => commit("yellow", "cw_unsure_reason")} className="rounded-2xl min-h-10 h-auto text-xs font-semibold hover:bg-slate-100 px-3 py-1.5 whitespace-normal leading-tight break-words">{t("cw_unsure")}</Button>
          </div>
        </div>
      )}

      {/* subtle progress */}
      <div className="flex items-center gap-1 pt-1" data-testid="cw-progress">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="h-1 flex-1 rounded-full" style={{ background: i <= step ? "#10B981" : "#E2E8F0" }} />
        ))}
      </div>
    </div>
  );
}
